import { Box, Breadcrumb, Flex, Heading, HStack, VStack, Image, Center, Badge } from "@chakra-ui/react"
import { SegmentedControl } from "@hydrologiq/shared/chakra/SegmentedControl"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"
import { EditableHeading } from "@hydrologiq/shared/chakra/EditableHeading"
import { useLocation, useNavigate, Outlet } from "react-router-dom"
import { useRoute } from "@hydrologiq/shared/providers/helpers/useRoute"
import { FaChevronRight } from "react-icons/fa6"
import Link from "../components/Link"
import "./Selection.scss"
import { useState, useEffect, useRef } from "react"
import ScrollToTop from "../ScrollToTop"

export interface SelectionItem {
  id: string
  name: string
  href?: string
  /**
   * When provided, the heading for this item becomes inline-editable and this
   * handler is called with the trimmed new name on save.
   */
  onRename?: (name: string) => void | Promise<void>
  /** True while a rename mutation is in flight (disables the controls). */
  renaming?: boolean
  /** Disables the rename affordance (e.g. not permitted). */
  renameDisabled?: boolean
  /** Tooltip explaining why renaming is disabled. */
  renameTooltip?: string
}

export interface SubNavItem {
  name: string
  href: string
  disabled?: boolean
}

export interface SiteLink {
  url: string
  name: string
}

interface SelectionProps {
  children?: React.ReactNode
  sectionHeading: string
  sectionBase: string
  item?: SelectionItem
  parents?: SelectionItem[]
  subNavItems?: SubNavItem[]
  subNavHome?: string
  branding?: string
  siteLink?: SiteLink
  siteLinkDeLink?: boolean
  badge?: string
}
const removeFirstSlash = (path: string) => (path.startsWith("/") ? path.slice(1) : path)
const addFirstSlash = (path: string) => (path.startsWith("/") ? path : `/${path}`)

function Selection(props: SelectionProps) {
  const { children, sectionHeading, item, parents = [], subNavItems = [], subNavHome, branding, siteLink, badge } = props
  let { sectionBase, siteLinkDeLink } = props
  sectionBase = removeFirstSlash(sectionBase)
  const route = useRoute()
  const navigate = useNavigate()

  let location = useLocation().pathname

  const [value, setValue] = useState<string | undefined>(undefined)

  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setValue(subNavHome ? `${location.endsWith("/") ? location : location + "/"}${subNavHome}` : location)
  }, [location, subNavHome])

  interface Breadcrumb {
    label: string
    href: string
  }

  const isSubSection = location.startsWith(sectionBase) || location.startsWith(`/${sectionBase}`)
  const isSectionHome = location === "/" || location === `/${sectionBase}`

  const normaliseHref = (href: string) => href.replace(/\/+/g, "/")

  const breadcrumbs: Breadcrumb[] = []
  if (!isSectionHome)
    breadcrumbs.push({
      label: sectionHeading,
      href: normaliseHref(
        isSubSection
          ? route.startsWith(sectionBase)
            ? removeFirstSlash(sectionBase)
            : addFirstSlash(sectionBase)
          : route
      ),
    })

  if (parents.length > 0) {
    breadcrumbs.push(
      ...parents.map((parent) => ({
        label: parent.name,
        href: normaliseHref(
          parent.href ?? (parent.id.startsWith("/") ? parent.id : `${route.startsWith("/") ? route : `/${route}`}/${parent.id}`)
        ),
      }))
    )
  }
  const sectionName = item ? item.name : sectionHeading

  const renderHeadingText = (name: string) => (
    <Tooltip content={name}>
      <Heading className="selection-heading" m={0}>
        {name.length > 50 ? `${name.slice(0, 50)}...` : name}
      </Heading>
    </Tooltip>
  )

  const renderHeading = () => {
    if (item?.onRename) {
      return (
        <EditableHeading
          value={sectionName}
          onSave={item.onRename}
          renderDisplay={renderHeadingText}
          saving={item.renaming}
          disabled={item.renameDisabled}
          disabledHelperText={item.renameTooltip}
          ariaLabel={`${sectionHeading.toLowerCase()} name`}
        />
      )
    }
    return renderHeadingText(sectionName)
  }

  return (
    <Box width="100%" color="primary" height="100%">
      <Box width="100%" overflowY="auto" className="view-wrapper" ref={containerRef}>
        <ScrollToTop container={containerRef} />
        <Flex
          pt="0.375rem"
          pb="0.375rem"
          flexShrink={0}
          height={{ base: "fit-content" }}
          width="100%"
          justifyContent="space-between"
          alignItems={{ base: "flex-start", md: breadcrumbs.length > 0 ? "flex-end" : "flex-start" }}
          flexDirection={{ base: "column", md: "row" }}
        >
          <VStack alignItems="flex-start">
            <Breadcrumb.Root variant={"plain"} height="26px">
              <Breadcrumb.List className="breadcrumb-trail" gap="1" alignItems="center">
                {breadcrumbs.map((breadcrumb, index) => (
                  <HStack key={`${breadcrumb.href}-${index}`} gap="1" alignItems="center">
                    <Breadcrumb.Item>
                      <Breadcrumb.Link
                        className="breadcrumb-link"
                        as="a"
                        href={normaliseHref(breadcrumb.href)}
                        onClick={(e: React.MouseEvent) => {
                          if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
                            e.preventDefault()
                            navigate(normaliseHref(breadcrumb.href))
                          }
                        }}
                      >
                        {breadcrumb.label}
                      </Breadcrumb.Link>
                    </Breadcrumb.Item>
                    {index < breadcrumbs.length - 1 && (
                      <Breadcrumb.Separator className="breadcrumb-separator">
                        <FaChevronRight size={10} />
                      </Breadcrumb.Separator>
                    )}
                  </HStack>
                ))}
              </Breadcrumb.List>
            </Breadcrumb.Root>
            <HStack gap={3} alignItems="center">
              {renderHeading()}
              {badge && (
                <Badge
                  size="sm"
                  variant="subtle"
                  bg="#EBF8FF"
                  color="#2B6CB0"
                  borderRadius="sm"
                  px={2}
                  py="2px"
                  fontSize="12px"
                  fontWeight="600"
                  lineHeight="normal"
                >
                  {badge}
                </Badge>
              )}
              {siteLink && (
                    <Box pt={"5px"}><Link href={siteLink.url} variant="secondary" fontSize="14px" internal={false} deLink={siteLinkDeLink}>
                      {siteLink.name}
                    </Link></Box>
                  )}
              {branding && (
                <Center height="100%" pt={1}>
                  <Image src={branding} alt="branding" height="15px" />
                </Center>
              )}
            </HStack>
          </VStack>
          <HStack
            flexDirection={{ base: "row-reverse", md: "row" }}
            justifyContent={{ base: "flex-end", md: "flex-start" }}
            alignSelf={{ base: "flex-start", md: "flex-end" }}
            width={{ base: "100%", md: "fit-content" }}
            height={subNavItems.length == 0 ? "43px" : undefined}
          >
            {subNavItems.length > 0 && (
              <SegmentedControl
                flexDirection={{ base: "row", md: "row" }}
                maxWidth={{ md: "fit-content" }}
                width={{ md: "fit-content" }}
                flexWrap={{ base: "wrap", md: "nowrap" }}
                minWidth={{ md: "fit-content" }}
                onValueChange={(details) => {
                  const item = subNavItems.find((item) => item.href === details.value)
                  if (item) {
                    setValue(item.href)
                    navigate(item.href)
                  }
                }}
                items={subNavItems.map((item) => ({
                  label: item.name,
                  value: item.href,
                  disabled: item.disabled,
                }))}
                value={value}
              />
            )}
          </HStack>
        </Flex>
        {children || <Outlet />}
      </Box>
    </Box>
  )
}

export default Selection
